import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, 
  Crown,
  Heart,
  RefreshCw,
  Share2,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Declaration() {
  const [declaration, setDeclaration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [declared, setDeclared] = useState(false);

  useEffect(() => {
    fetchDeclaration();
  }, []);

  const fetchDeclaration = async () => {
    try {
      const res = await axios.get(`${API}/declaration/today`);
      setDeclaration(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load declaration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclare = () => {
    setDeclared(true);
    toast.success('You declared your truth! Keep wearing that crown, Queen.');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Crown Declaration',
          text: `"${declaration.declaration}"\n\n${declaration.scripture}\n\n— Reclaiming My Crown`,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(
        `"${declaration.declaration}"\n\n${declaration.scripture}\n\n— Reclaiming My Crown`
      );
      toast.success('Declaration copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="declaration-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 mb-6">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            Daily Crown Declaration
          </h1>
          <p className="text-muted-foreground">
            Speak truth over your life every day. Words have power.
          </p>
        </div>

        {/* Date Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-primary">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">
              {new Date(declaration?.date).toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          </div>
        </div>

        {/* Declaration Card */}
        <div className="relative" data-testid="declaration-card">
          <div className="glass-gold rounded-3xl p-8 lg:p-12 text-center">
            {/* Crown Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gold-gradient mb-6 animate-pulse-gold">
              <Crown className="w-8 h-8 text-purple-950" />
            </div>
            
            {/* Declaration Text */}
            <h2 className="font-serif text-2xl lg:text-3xl font-bold text-foreground mb-6 leading-relaxed">
              "{declaration?.declaration}"
            </h2>
            
            {/* Scripture */}
            <div className="bg-white/60 dark:bg-black/20 rounded-xl p-6 mb-8">
              <Heart className="w-5 h-5 text-purple-500 mx-auto mb-3" />
              <p className="font-scripture text-lg italic text-foreground">
                {declaration?.scripture}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!declared ? (
                <Button
                  onClick={handleDeclare}
                  data-testid="declare-btn"
                  className="btn-gold rounded-full px-10 py-6 text-lg font-semibold"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  I Declare This Over My Life
                </Button>
              ) : (
                <div className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  <Check className="w-5 h-5" />
                  <span className="font-semibold">Declared Today!</span>
                </div>
              )}
              <Button
                onClick={handleShare}
                variant="outline"
                data-testid="share-btn"
                className="rounded-full px-8 py-6 border-2 border-primary text-primary hover:bg-primary/5"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* Encouragement */}
        <div className="mt-10 text-center">
          <p className="text-muted-foreground text-sm">
            Come back tomorrow for a new declaration. Consistency is key to rewiring your mind.
          </p>
        </div>

        {/* Tips Section */}
        <div className="mt-10 crown-card p-6">
          <h3 className="font-semibold text-foreground mb-4">How to Use This Practice</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">1</span>
              <span>Read the declaration out loud. Speaking truth activates different parts of your brain than just reading.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">2</span>
              <span>Place your hand on your heart as you say it. Physical connection enhances the emotional impact.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">3</span>
              <span>Say it even if you don't believe it yet. Repetition creates new neural pathways.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">4</span>
              <span>Return to it throughout the day when doubts arise. Let it be your anchor.</span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
